/**
 * Prompt 模板库页面 — 完整 CRUD
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Modal, Input, Space, Popconfirm, message, Empty, Tag, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, BookOutlined, CopyOutlined } from '@ant-design/icons';
import { getTemplates, createTemplate, updateTemplate, deleteTemplate } from '../services/api';

const { TextArea } = Input;

const TemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formName, setFormName] = useState('');
  const [formContent, setFormContent] = useState('');
  const [saving, setSaving] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || 'null');

  const loadTemplates = useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);
      const res = await getTemplates();
      if (res.success) setTemplates(res.templates || []);
    } catch {
      message.error('加载模板失败');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { loadTemplates(); }, []);

  const openCreateModal = () => {
    setEditingTemplate(null);
    setFormName('');
    setFormContent('');
    setModalVisible(true);
  };

  const openEditModal = (record) => {
    setEditingTemplate(record);
    setFormName(record.template_name);
    setFormContent(record.template_content);
    setModalVisible(true);
  };

  const handleSave = async () => {
    if (!formName.trim()) { message.warning('请输入模板名称'); return; }
    if (!formContent.trim()) { message.warning('请输入模板内容'); return; }
    try {
      setSaving(true);
      if (editingTemplate) {
        await updateTemplate(editingTemplate.template_id, { name: formName, content: formContent });
        message.success('模板已更新');
      } else {
        await createTemplate({ name: formName, content: formContent });
        message.success('模板已创建');
      }
      setModalVisible(false);
      loadTemplates();
    } catch {
      message.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteTemplate(id);
      message.success('模板已删除');
      loadTemplates();
    } catch {
      message.error('删除失败');
    }
  };

  const handleCopy = (content) => {
    navigator.clipboard.writeText(content);
    message.success('已复制到剪贴板');
  };

  const columns = [
    {
      title: '模板名称',
      dataIndex: 'template_name',
      key: 'template_name',
      width: 200,
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: '内容预览',
      dataIndex: 'template_content',
      key: 'template_content',
      ellipsis: true,
      render: (text) => <span style={{ color: 'var(--color-text-secondary)' }}>{text}</span>,
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 180,
      render: (text) => text ? new Date(text).toLocaleString('zh-CN') : '-',
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title="复制"><Button size="small" icon={<CopyOutlined />} onClick={() => handleCopy(record.template_content)} /></Tooltip>
          <Tooltip title="编辑"><Button size="small" icon={<EditOutlined />} onClick={() => openEditModal(record)} /></Tooltip>
          <Popconfirm title="确定删除此模板？" onConfirm={() => handleDelete(record.template_id)} okText="删除" cancelText="取消">
            <Tooltip title="删除"><Button size="small" danger icon={<DeleteOutlined />} /></Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (!user) {
    return (
      <Card>
        <Empty image={<BookOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />} description="请先登录后使用模板库" />
      </Card>
    );
  }

  return (
    <div className="templates-page">
      <Card
        title="Prompt 模板库"
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>新建模板</Button>}
      >
        {templates.length === 0 && !loading ? (
          <Empty
            image={<BookOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
            description="暂无模板"
          >
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>创建第一个模板</Button>
          </Empty>
        ) : (
          <Table
            dataSource={templates}
            columns={columns}
            rowKey="template_id"
            loading={loading}
            pagination={false}
            scroll={{ x: 700 }}
          />
        )}
      </Card>

      <Modal
        title={editingTemplate ? '编辑模板' : '新建模板'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        confirmLoading={saving}
        okText="保存"
        cancelText="取消"
        width={640}
      >
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 8 }}>模板名称</label>
          <Input placeholder="例如：情感分析、分类打标" value={formName} onChange={e => setFormName(e.target.value)} maxLength={100} />
        </div>
        <div>
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 8 }}>模板内容</label>
          <TextArea rows={10} placeholder="输入 Prompt 模板内容..." value={formContent} onChange={e => setFormContent(e.target.value)} />
        </div>
      </Modal>
    </div>
  );
};

export default TemplatesPage;
